import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import {
  Loader2,
  Search,
  History,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileText,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ExternalLink,
  Download,
} from "lucide-react";
import {
  adminListLabourAudit,
  adminListLabourAuditActors,
  adminGetLabourAuditDetail,
} from "@/lib/api/labour-analyzer.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const AUDIT_STATUS_OPTIONS = [
  { value: "all", label: "All events" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "admin_review", label: "Decision: admin review" },
  { value: "analyzing", label: "Decision: analyzing" },
  { value: "archived", label: "Decision: archived" },
  { value: "uploaded", label: "Uploaded" },
  { value: "analysed", label: "Analysed" },
  { value: "rerun_requested", label: "Re-run requested" },
  { value: "analysis_failed", label: "Analysis failed" },
  { value: "form_7_11_completed", label: "Form 7.11 completed" },
  { value: "admin_update", label: "Admin update" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

const auditSearchSchema = z.object({
  dateFrom: fallback(z.string(), "").default(""),
  dateTo: fallback(z.string(), "").default(""),
  status: fallback(z.string(), "all").default("all"),
  actorId: fallback(z.string(), "").default(""),
  documentId: fallback(z.string(), "").default(""),
  search: fallback(z.string(), "").default(""),
  page: fallback(z.number().int().min(1), 1).default(1),
  pageSize: fallback(z.number().int().min(5).max(500), 50).default(50),
  sortBy: fallback(z.enum(["created_at", "action", "actor_id"]), "created_at").default("created_at"),
  sortDir: fallback(z.enum(["asc", "desc"]), "desc").default("desc"),
});

export const Route = createFileRoute("/_authenticated/portal/admin/labour-analyses-audit")({
  head: () => ({
    meta: [{ title: "Admin · Approval audit trail" }, { name: "robots", content: "noindex" }],
  }),
  validateSearch: zodValidator(auditSearchSchema),
  component: AdminAuditTrail,
});

function AdminAuditTrail() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/portal/admin/labour-analyses-audit" });
  const fetchAudit = useServerFn(adminListLabourAudit);
  const fetchActors = useServerFn(adminListLabourAuditActors);
  const fetchDetail = useServerFn(adminGetLabourAuditDetail);

  const [local, setLocal] = useState({
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    status: search.status,
    actorId: search.actorId,
    documentId: search.documentId,
    search: search.search,
  });
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  const offset = (search.page - 1) * search.pageSize;

  const actorsQ = useQuery({
    queryKey: ["labour-audit-actors"],
    queryFn: () => fetchActors({ data: {} }),
  });

  const auditQ = useQuery({
    queryKey: ["admin-labour-audit", search],
    queryFn: () =>
      fetchAudit({
        data: {
          dateFrom: search.dateFrom || null,
          dateTo: search.dateTo || null,
          status: search.status || "all",
          actorId: search.actorId || null,
          documentId: search.documentId || null,
          search: search.search || null,
          limit: search.pageSize,
          offset,
          sortBy: search.sortBy,
          sortDir: search.sortDir,
        },
      }),
  });

  const detailQ = useQuery({
    queryKey: ["admin-labour-audit-detail", openEventId],
    queryFn: () => fetchDetail({ data: { id: openEventId! } }),
    enabled: !!openEventId,
  });

  function applyFilters() {
    navigate({ search: { ...search, ...local, page: 1 } });
  }

  function clearFilters() {
    const empty = { dateFrom: "", dateTo: "", status: "all", actorId: "", documentId: "", search: "" };
    setLocal(empty);
    navigate({ search: { ...search, ...empty, page: 1 } });
  }

  function setPage(page: number) {
    navigate({ search: { ...search, page } });
  }

  function setPageSize(size: number) {
    navigate({ search: { ...search, pageSize: size, page: 1 } });
  }

  function toggleSort(col: "created_at" | "action" | "actor_id") {
    const sortDir = search.sortBy === col && search.sortDir === "desc" ? "asc" : "desc";
    navigate({ search: { ...search, sortBy: col, sortDir, page: 1 } });
  }

  function SortIcon({ col }: { col: "created_at" | "action" | "actor_id" }) {
    if (search.sortBy !== col) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-50" />;
    return search.sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  }

  const hasActiveFilters =
    search.dateFrom || search.dateTo || search.status !== "all" || search.actorId || search.documentId || search.search;

  return (
    <div className="space-y-6">
      <Link
        to={"/portal/admin/labour-analyses" as any}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All matters
      </Link>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <History className="h-4 w-4" /> Admin · Labour Law
        </div>
        <h1 className="font-display text-2xl text-navy">Approval audit trail</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Filter, sort and search every approval, rejection and decision note. Click any row for the
          full decision context, AI report version and attached document links.
        </p>
      </header>

      <section className="rounded-xl border border-border bg-card p-4 shadow-soft">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Date from</Label>
            <Input
              type="date"
              value={local.dateFrom}
              onChange={(e) => setLocal((s) => ({ ...s, dateFrom: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date to</Label>
            <Input
              type="date"
              value={local.dateTo}
              onChange={(e) => setLocal((s) => ({ ...s, dateTo: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status / action</Label>
            <Select value={local.status} onValueChange={(v) => setLocal((s) => ({ ...s, status: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Approver / rejector</Label>
            <Select value={local.actorId} onValueChange={(v) => setLocal((s) => ({ ...s, actorId: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Any actor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any actor</SelectItem>
                {(actorsQ.data?.actors ?? []).map((a: any) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.full_name || a.email || a.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Document ID</Label>
            <Input
              value={local.documentId}
              onChange={(e) => setLocal((s) => ({ ...s, documentId: e.target.value }))}
              placeholder="Matter UUID"
            />
          </div>
          <div className="space-y-1 sm:col-span-2 lg:col-span-3">
            <Label className="text-xs">Search action, actor or note</Label>
            <Input
              value={local.search}
              onChange={(e) => setLocal((s) => ({ ...s, search: e.target.value }))}
              placeholder="Search action, actor name, or decision note…"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={applyFilters} className="bg-navy hover:bg-navy/90">
            <Search className="mr-2 h-4 w-4" /> Search / apply
          </Button>
          <Button variant="outline" onClick={clearFilters} disabled={!hasActiveFilters}>
            <X className="mr-2 h-4 w-4" /> Clear
          </Button>
          {hasActiveFilters && (
            <span className="text-xs text-muted-foreground">Filters active</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Rows per page</Label>
            <Select
              value={String(search.pageSize)}
              onValueChange={(v) => setPageSize(Number(v))}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {auditQ.isLoading ? (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : auditQ.data?.rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No audit events match your filters.
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => toggleSort("created_at")}
                    className="inline-flex items-center hover:text-navy"
                  >
                    When <SortIcon col="created_at" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => toggleSort("action")}
                    className="inline-flex items-center hover:text-navy"
                  >
                    Action <SortIcon col="action" />
                  </button>
                </TableHead>
                <TableHead>Matter</TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => toggleSort("actor_id")}
                    className="inline-flex items-center hover:text-navy"
                  >
                    Actor <SortIcon col="actor_id" />
                  </button>
                </TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditQ.data!.rows.map((r: any) => {
                const isDecision = String(r.action).startsWith("decision_");
                const verdict = isDecision ? String(r.action).replace("decision_", "") : null;
                return (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer"
                    onClick={() => setOpenEventId(r.id)}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString("en-ZA")}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          isDecision && verdict === "approved"
                            ? "bg-emerald-100 text-emerald-900"
                            : isDecision && verdict === "rejected"
                            ? "bg-rose-100 text-rose-900"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isDecision ? verdict!.toUpperCase() : r.action}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs text-navy">
                      {r.matter?.matter_title ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.actor ? r.actor.full_name || r.actor.email : "system"}
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate text-xs text-foreground/80">
                      {r.details?.decision_notes ?? ""}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenEventId(r.id);
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {auditQ.data && auditQ.data.total > 0 && (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-border pt-4 text-xs sm:flex-row">
          <p className="text-muted-foreground">
            Showing {offset + 1}-{Math.min(offset + search.pageSize, auditQ.data.total)} of{" "}
            {auditQ.data.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(search.page - 1)}
              disabled={search.page <= 1}
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <span className="text-muted-foreground">Page {search.page}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(search.page + 1)}
              disabled={offset + search.pageSize >= auditQ.data.total}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Sheet open={!!openEventId} onOpenChange={(o) => !o && setOpenEventId(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Audit event detail</SheetTitle>
            <SheetDescription>
              Full decision context, AI report version and attached document links.
            </SheetDescription>
          </SheetHeader>

          {detailQ.isLoading ? (
            <p className="mt-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : detailQ.data ? (
            <AuditDetailBody data={detailQ.data} onClose={() => setOpenEventId(null)} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function AuditDetailBody({ data, onClose }: { data: any; onClose: () => void }) {
  const { event, matter, client, actor, drafts, downloadUrl, recentTimeline } = data;
  const isDecision = String(event.action).startsWith("decision_");
  const verdict = isDecision ? String(event.action).replace("decision_", "") : null;
  const report = matter?.ai_report ?? null;

  return (
    <div className="mt-6 space-y-5 text-sm">
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              verdict === "approved"
                ? "bg-emerald-100 text-emerald-900"
                : verdict === "rejected"
                ? "bg-rose-100 text-rose-900"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {isDecision ? verdict!.toUpperCase() : event.action}
          </span>
          <span className="text-xs text-muted-foreground">
            {new Date(event.created_at).toLocaleString("en-ZA")}
          </span>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Actor: <span className="text-navy">{actor?.full_name || actor?.email || "system"}</span>
          {event.actor_role && <> · Role: {event.actor_role}</>}
        </p>
        {event.details?.decision_notes && (
          <p className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-card p-2 text-xs">
            “{event.details.decision_notes}”
          </p>
        )}
        {event.details && (
          <details className="mt-2 text-[11px] text-muted-foreground">
            <summary className="cursor-pointer">Raw event payload</summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-[10px]">
              {JSON.stringify(event.details, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {matter ? (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Matter
          </p>
          <p className="font-semibold text-navy">{matter.matter_title}</p>
          <p className="text-xs text-muted-foreground">
            {matter.doc_type?.replaceAll("_", " ")} · Status {matter.status} ·{" "}
            {matter.student_number ?? "—"}
          </p>
          {client && (
            <p className="text-xs">
              Client: <span className="text-navy">{client.full_name || client.email}</span>
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Link
              to={"/portal/admin/labour-analyses/$id" as any}
              params={{ id: matter.id } as any}
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy"
            >
              <ExternalLink className="h-3 w-3" /> Open matter
            </Link>
            {downloadUrl && (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy"
              >
                <Download className="h-3 w-3" /> Original document
              </a>
            )}
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
          This event is not linked to a matter.
        </p>
      )}

      {report && (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            AI report version
          </p>
          <p className="text-[11px] text-muted-foreground">
            Model: {matter.ai_model ?? "—"} · Confidence: {matter.ai_confidence ?? "—"}%
          </p>
          {report.matter_summary && (
            <p className="text-xs text-foreground/90">{report.matter_summary}</p>
          )}
          {report.risk_rating && (
            <p className="text-xs">
              Risk: <strong className="text-navy">{report.risk_rating}</strong>
            </p>
          )}
          <details className="text-[11px] text-muted-foreground">
            <summary className="cursor-pointer">View full AI report JSON</summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted p-2 text-[10px]">
              {JSON.stringify(report, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Attached drafts ({drafts.length})
          </p>
          <ul className="space-y-1">
            {drafts.map((d: any) => (
              <li key={d.id} className="flex items-center gap-2 text-xs">
                <FileText className="h-3 w-3 text-gold" />
                <span className="text-navy">{d.title}</span>
                <span className="text-muted-foreground">· {d.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentTimeline.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Matter timeline
          </p>
          <ul className="space-y-1.5">
            {recentTimeline.map((t: any) => (
              <li
                key={t.id}
                className={`text-[11px] ${
                  t.id === event.id ? "rounded bg-gold/10 p-1 font-semibold text-navy" : ""
                }`}
              >
                <span className="text-muted-foreground">
                  {new Date(t.created_at).toLocaleString("en-ZA")}
                </span>{" "}
                · {t.action}
                {t.details?.decision_notes && (
                  <span className="text-foreground/70"> — “{t.details.decision_notes}”</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
